import { unescapeBodyHeadings } from "@libertify/ui";
import type { SerializableKeyPoint, SummaryDocument } from "@libertify/ui";

export type KeyPointRow = {
  /** Session-only id: titles are editable and may repeat, so not the slug. */
  id: string;
  title: string;
  body: string;
  level: number;
};

/** Level used for points the editor creates; see DEFAULT_HEADING_LEVEL. */
const NEW_POINT_LEVEL = 2;

/** Session-only row ids: unique for the life of the page is all they need. */
let nextRowId = 0;
const newRowId = (): string => `key-point-${++nextRowId}`;

export const newPointRow = (): KeyPointRow => ({
  id: newRowId(),
  title: "",
  body: "",
  level: NEW_POINT_LEVEL,
});

/**
 * The stored document as editable rows.
 *
 * A summary with no sections at all parses as pure preamble — rather than
 * showing an empty card over real content, fold it into a single point the
 * user can then split up. Its title is left empty on purpose: a titleless
 * point serializes back as plain prose, so merely editing the text doesn't
 * turn the viewer's summary into an accordion. Typing a title is how the
 * user opts into that.
 */
export const toInitial = (
  document: SummaryDocument
): { preamble: string; rows: KeyPointRow[] } => {
  if (document.points.length === 0 && document.preamble.trim()) {
    return {
      preamble: "",
      rows: [{ ...newPointRow(), body: unescapeBodyHeadings(document.preamble) }],
    };
  }
  return {
    preamble: document.preamble,
    rows: document.points.map((point) => ({
      id: newRowId(),
      title: point.title,
      // The serializer escapes headings typed into a body; show them as typed.
      body: unescapeBodyHeadings(point.body),
      level: point.level,
    })),
  };
};

/**
 * What a row saves as, given the version the backend holds.
 *
 * A titleless point serializes as bare prose, which re-parses as the body of
 * the point above it. So a cleared title keeps its saved one until a new one
 * is typed, and a new point waits for a title. A point cleared entirely is
 * removed.
 */
export const resolveRow = (
  row: Partial<KeyPointRow>,
  saved: KeyPointRow | undefined
): KeyPointRow | null => {
  const title = row.title ?? "";
  const body = row.body ?? "";
  if (!saved) {
    if (!title.trim()) return null;
    return {
      id: row.id ?? newRowId(),
      level: row.level ?? NEW_POINT_LEVEL,
      title,
      body,
    };
  }
  if (!title.trim() && !body.trim()) return null;
  return { ...saved, ...row, title: title.trim() ? title : saved.title, body };
};

/**
 * Gives the folded prose point a title once the document has titled ones.
 * Untitled, it would serialize as preamble, which the viewer's accordion drops.
 */
export const titleUntitledPoints = (
  points: SerializableKeyPoint[],
  fallbackTitle: string
): SerializableKeyPoint[] =>
  points.some((point) => point.title.trim())
    ? points.map((point) =>
        point.title.trim() ? point : { ...point, title: fallbackTitle }
      )
    : points;

/** The rows as the serializer takes them: the session ids dropped. */
export const toWire = (rows: KeyPointRow[]): SerializableKeyPoint[] =>
  rows.map(({ title, body, level }) => ({ title, body, level }));
